import * as Sentry from '@sentry/react';
import { dumpLogs, pushLogEntry } from './logBuffer';

// Expose in production for on-demand log dumping from the browser console:
//   window.__dumpLogs()
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__dumpLogs = dumpLogs;
}

export interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

function toBreadcrumbMessage(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.message;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function addBreadcrumb(module: string, level: 'debug' | 'info' | 'warning' | 'error', args: unknown[]): void {
  Sentry.addBreadcrumb({
    category: module,
    level,
    message: args.map(toBreadcrumbMessage).join(' '),
    timestamp: Date.now() / 1000
  });
}

export function createLogger(module: string): Logger {
  const isDev = import.meta.env.DEV;

  return {
    debug: (...args: unknown[]) => {
      const message = args.map(toBreadcrumbMessage).join(' ');
      pushLogEntry({ ts: Date.now(), level: 'debug', module, message });
      addBreadcrumb(module, 'debug', args);
      if (isDev) {
        console.debug(`[${module}]`, ...args);
      }
    },
    info: (...args: unknown[]) => {
      const message = args.map(toBreadcrumbMessage).join(' ');
      pushLogEntry({ ts: Date.now(), level: 'info', module, message });
      addBreadcrumb(module, 'info', args);
      if (isDev) {
        console.info(`[${module}]`, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      const message = args.map(toBreadcrumbMessage).join(' ');
      pushLogEntry({ ts: Date.now(), level: 'warn', module, message });
      addBreadcrumb(module, 'warning', args);
      console.warn(`[${module}]`, ...args);
    },
    error: (...args: unknown[]) => {
      const message = args.map(toBreadcrumbMessage).join(' ');
      pushLogEntry({ ts: Date.now(), level: 'error', module, message });
      addBreadcrumb(module, 'error', args);
      console.error(`[${module}]`, ...args);
    }
  };
}
